import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

import { Separator } from "@/components/ui/separator";
import {
	completionTrendQueryOptions,
	dashboardStatsQueryOptions,
	versionDifficultyQueryOptions,
} from "@/hooks/query/dashboard";
import * as m from "@/paraglide/messages";

import { CompletionTrendChart } from "./-components/completion-trend-chart";
import { QuestionAccuracyTable } from "./-components/question-accuracy-table";
import { StatCards } from "./-components/stat-cards";
import { StudentPerformanceTable } from "./-components/student-performance-table";
import { StudentRankingTable } from "./-components/student-ranking-table";
import { TimeRangeSelect } from "./-components/time-range-select";
import { VersionDifficultyChart } from "./-components/version-difficulty-chart";

export const Route = createFileRoute("/(app)/dashboard/")({
	component: DashboardPage,
	beforeLoad: ({ context, matches }) => {
		const user = context.user;
		const pathname = matches
			.map((match) => match.pathname.slice(0, match.pathname.length - 1))
			.join("");

		if (!user || user.isAnonymous) {
			throw redirect({ to: "/sign-in", search: { from: pathname } });
		}
	},
});

type TimeRange = "7d" | "30d" | "90d";

function DashboardPage() {
	const [timeRange, setTimeRange] = useState<TimeRange>("30d");

	const { data: stats, isPending: isStatsPending } = useQuery(
		dashboardStatsQueryOptions(timeRange),
	);
	const { data: trend, isPending: isTrendPending } = useQuery(
		completionTrendQueryOptions(timeRange),
	);
	const { data: difficulty, isPending: isDifficultyPending } = useQuery(
		versionDifficultyQueryOptions(),
	);

	return (
		<div className="space-y-6 p-6">
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-medium">{m.sidebar_dashboard()}</h3>
					<p className="text-sm text-muted-foreground">查看学生的学习完成情况</p>
				</div>
				<TimeRangeSelect value={timeRange} onChange={setTimeRange} />
			</div>
			<Separator />

			<StatCards data={stats} isPending={isStatsPending} />

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<CompletionTrendChart data={trend} isPending={isTrendPending} />
				<VersionDifficultyChart data={difficulty} isPending={isDifficultyPending} />
			</div>

			<StudentRankingTable timeRange={timeRange} />

			<QuestionAccuracyTable timeRange={timeRange} />

			<StudentPerformanceTable />
		</div>
	);
}
